import React, { useState, useMemo } from 'react';
import UserCard from './UserCard';
import UserFilters from './UserFilters';

/**
 * Componente principal para la lista de usuarios
 */
const UserList = ({ 
  usuarios, 
  modulos, 
  permisos, 
  onEditPermissions, 
  canEditUsers,
  loading 
}) => {
  // Estados para filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [moduleFilter, setModuleFilter] = useState('all');

  // Función para filtrar usuarios
  const filteredUsers = useMemo(() => {
    if (!usuarios) return [];

    return usuarios.filter(usuario => {
      // Filtro por búsqueda (nombre, email)
      const searchMatch = searchTerm === '' || 
        (usuario.displayName || usuario.nombre || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (usuario.email || '').toLowerCase().includes(searchTerm.toLowerCase());

      // Filtro por estado
      const statusMatch = statusFilter === 'all' ||
        (statusFilter === 'active' && usuario.activo) ||
        (statusFilter === 'inactive' && !usuario.activo);

      // Filtro por módulo (usuario tiene permisos en el módulo seleccionado)
      const moduleMatch = moduleFilter === 'all' ||
        (usuario.modulos && usuario.modulos[moduleFilter] && 
         usuario.modulos[moduleFilter].permisos && 
         usuario.modulos[moduleFilter].permisos.length > 0);

      return searchMatch && statusMatch && moduleMatch;
    });
  }, [usuarios, searchTerm, statusFilter, moduleFilter]);

  // Handlers para filtros
  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const handleStatusFilterChange = (e) => setStatusFilter(e.target.value);
  const handleModuleFilterChange = (e) => setModuleFilter(e.target.value);

  if (loading) {
    return (
      <div style={{ 
        textAlign: 'center', 
        padding: '60px 20px',
        color: '#6c757d' 
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #007bff',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px'
        }}></div>
        <p>Cargando usuarios...</p>
      </div>
    );
  }

  return (
    <div>
      {/* CSS para animaciones */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      
      {/* Filtros */}
      <UserFilters
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        statusFilter={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
        moduleFilter={moduleFilter}
        onModuleFilterChange={handleModuleFilterChange}
        modulos={modulos}
        totalUsers={usuarios?.length || 0}
        filteredUsers={filteredUsers.length}
      />

      {/* Lista de usuarios */}
      <div style={{ marginTop: '20px' }}>
        {filteredUsers.length > 0 ? (
          filteredUsers.map(usuario => (
            <UserCard
              key={usuario.id}
              usuario={usuario}
              modulos={modulos}
              permisos={permisos}
              onEditPermissions={onEditPermissions}
              canEditUsers={canEditUsers}
            />
          ))
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '60px 20px',
            color: '#6c757d',
            backgroundColor: 'white',
            borderRadius: '8px',
            border: '1px solid #dee2e6'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>👥</div>
            <h5 style={{ marginBottom: '8px' }}>No se encontraron usuarios</h5>
            <p style={{ margin: '0', fontSize: '14px' }}>
              {searchTerm || statusFilter !== 'all' || moduleFilter !== 'all' 
                ? 'Intenta ajustar los filtros para ver más resultados'
                : 'No hay usuarios registrados en el sistema'
              }
            </p>
          </div>
        )}
      </div>

      {/* Información adicional */}
      {filteredUsers.length > 0 && (
        <div style={{
          marginTop: '20px',
          padding: '12px',
          backgroundColor: '#f8f9fa',
          borderRadius: '6px',
          fontSize: '12px',
          color: '#6c757d',
          border: '1px solid #dee2e6'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>
              Mostrando {filteredUsers.length} de {usuarios?.length || 0} usuarios
            </span>
            <span>
              💡 Haz clic en los badges de módulos para editar permisos específicos
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
